/**
 * The household auto-reload editor (Plan 09).
 *
 * Extracted out of `balance-card.tsx` so it is the ONE auto-reload enablement
 * surface in the app: the balance card opens it, and so do the auto-order
 * wizard + auto-order detail page when the BALANCE requirement
 * (Tasker 0e05d6de) is unmet. Duplicating threshold/amount inputs per caller is
 * how those callers drift apart on defaults and validation.
 *
 * The dialog owns nothing but the draft: saving PATCHes
 * `/api/billing/auto-reload`, which invalidates `['billing','family']`, so every
 * caller's `useBillingFamilyQuery` re-reads and its gate re-evaluates. `onSaved`
 * exists only for callers that need to close/refetch something extra.
 *
 * Turning it OFF is a two-step flow, not a refusal. When active balance-funded
 * auto-orders still depend on auto-reload the server 409s and names them
 * (`auto_reload_required_by_subscriptions`); Save then raises the app's standard
 * destructive confirmation, and confirming re-sends with
 * `pauseAffectedSubscriptions: true` so the server pauses those auto-orders AND
 * disables auto-reload in one transaction. Cancel sends nothing and changes
 * nothing — including this dialog's own draft.
 *
 * The decision half lives in `lib/auto-reload-disable.ts` (pure, unit-tested);
 * this file is only the wiring.
 */

import { smallestPresetAtLeast, topUpPresetsForMinimum } from '@ordo/constants/wallet'
import { Button } from '@ordo/ui/button'
import { useDestructiveConfirm } from '@ordo/ui/destructive-confirm'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@ordo/ui/input-group'
import { Label } from '@ordo/ui/label'
import { SimpleDialog } from '@ordo/ui/simple-dialog'
import { toast } from '@ordo/ui/sonner'
import { Switch } from '@ordo/ui/switch'
import { type FormEvent, useEffect, useState } from 'react'

import { type BillingFamilyAccount, useSetAutoReloadMutation } from '@/api/billing-family'
import { autoReloadSaveToastMessage, saveAutoReloadWithConfirm } from '@/lib/auto-reload-disable'

import { AmountPresetGrid } from './amount-preset-grid'
import {
  AUTO_RELOAD_TRIGGER_SENTENCE,
  DEFAULT_AUTO_RELOAD_AMOUNT_CENTS,
  DEFAULT_AUTO_RELOAD_THRESHOLD_CENTS,
  formatCents,
} from './helpers'

/**
 * The dialog only ever offers quick amounts, but the stored value is not
 * guaranteed to be one of them, for two reasons now. A stored legacy value
 * (from before presets, or pre-normalization-script) may be anything; and
 * since PROD-1317 a school can RAISE its minimum load-up out from under an
 * amount that was perfectly valid when it was saved, which the auto-reload
 * validator would then reject on the next save. Both seed to the smallest
 * OFFERED amount that covers the stored one, so the draft is always a
 * selection the server still accepts.
 *
 * A disabled or never-configured account (`storedCents <= 0`) seeds from
 * the platform default instead — itself lifted to the floor, because $100
 * is not a legal re-load at a school with a $200 minimum.
 *
 * Exported for its unit test; the dialog is the only caller.
 */
export function seedAmountCents(storedCents: number, minTopUpCents: number): number {
  const offered = topUpPresetsForMinimum(minTopUpCents)
  const target = storedCents > 0 ? storedCents : DEFAULT_AUTO_RELOAD_AMOUNT_CENTS
  // Nothing on offer covers the stored amount (it sits past the largest
  // quick amount): the biggest OFFERED one is the closest legal draft —
  // never the largest preset outright, which a raised minimum may exclude.
  return smallestPresetAtLeast(target, minTopUpCents) ?? offered[offered.length - 1]
}

export interface AutoReloadDialogProps {
  open: boolean
  onClose: () => void
  account: BillingFamilyAccount
  /** Fired after a successful save. Callers use it to close + refetch. */
  onSaved: () => void
  /**
   * Copy under the title. Callers that open this from an auto-order flow say
   * why it's required; the balance card keeps the generic wallet explanation.
   */
  description?: string
}

export function AutoReloadDialog({ open, onClose, account, onSaved, description }: AutoReloadDialogProps) {
  const [enabled, setEnabled] = useState(account.autoReload)
  const [threshold, setThreshold] = useState(
    (account.autoReloadThresholdCents ?? DEFAULT_AUTO_RELOAD_THRESHOLD_CENTS) / 100,
  )
  const [amountCents, setAmountCents] = useState(seedAmountCents(account.autoReloadAmountCents, account.minTopUpCents))

  // Re-seed the draft each time the dialog opens (or the account changes) so a
  // reopen never shows a stale toggle after someone else changed the setting.
  // `minTopUpCents` is in here for the same reason: a school that raised its
  // minimum has invalidated the stored amount, and the re-seed is what lifts
  // the draft back onto a legal one.
  useEffect(() => {
    if (!open) {
      return
    }
    setEnabled(account.autoReload)
    setThreshold((account.autoReloadThresholdCents ?? DEFAULT_AUTO_RELOAD_THRESHOLD_CENTS) / 100)
    setAmountCents(seedAmountCents(account.autoReloadAmountCents, account.minTopUpCents))
  }, [
    open,
    account.id,
    account.autoReload,
    account.autoReloadThresholdCents,
    account.autoReloadAmountCents,
    account.minTopUpCents,
  ])

  const setAutoReloadMutation = useSetAutoReloadMutation()
  const confirm = useDestructiveConfirm()
  const loading = setAutoReloadMutation.isPending

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      const outcome = await saveAutoReloadWithConfirm(
        {
          accountId: account.id,
          enabled,
          thresholdCents: enabled ? Math.round(threshold * 100) : null,
          reloadAmountCents: enabled ? amountCents : null,
        },
        {
          save: (vars) => setAutoReloadMutation.mutateAsync(vars),
          confirm: (copy) =>
            confirm({
              title: copy.title,
              body: copy.body,
              confirmLabel: copy.confirmLabel,
              cancelLabel: copy.cancelLabel,
            }),
        },
      )
      // Declining the confirmation leaves the server untouched AND the draft
      // exactly as the parent left it — no toast, no forced toggle flip.
      if (outcome.status === 'CANCELLED') {
        return
      }
      toast.success(autoReloadSaveToastMessage({ enabled, pausedCount: outcome.pausedCount }))
      onSaved()
    } catch (err) {
      toast.error('Could not update auto-reload', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <SimpleDialog
      open={open}
      onClose={onClose}
      title="Auto-reload"
      description={description ?? `Keep your wallet topped up automatically. ${AUTO_RELOAD_TRIGGER_SENTENCE}`}
      maxWidth="md">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 p-3">
          <div className="flex flex-col">
            <span className="text-sm font-medium">Enabled</span>
            <span className="text-xs text-muted-foreground">
              {enabled ? 'We will re-load your balance automatically.' : 'No automatic re-loads.'}
            </span>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Re-load amount</Label>
          <AmountPresetGrid
            presetsCents={topUpPresetsForMinimum(account.minTopUpCents)}
            value={amountCents}
            onSelect={setAmountCents}
            currency={account.currency}
            disabled={!enabled}
          />
          <span className="text-xs text-muted-foreground">How much to add each time.</span>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="auto-reload-threshold">Threshold</Label>
          <InputGroup>
            <InputGroupAddon>$</InputGroupAddon>
            <InputGroupInput
              id="auto-reload-threshold"
              type="number"
              min="0"
              step="0.01"
              disabled={!enabled}
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
            />
          </InputGroup>
          <span className="text-xs text-muted-foreground">Re-load when balance drops below this amount.</span>
        </div>

        {!account.defaultPaymentMethodRef ? (
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            Add a default payment method on the Billing page first — auto-reload charges that card.
          </div>
        ) : (
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            {enabled
              ? `We'll add ${formatCents(amountCents, account.currency)} if your balance drops below ${formatCents(
                  Math.round(threshold * 100),
                  account.currency,
                )}.`
              : "Auto-reload is off. You'll need to manually add funds before placing orders if your balance is too low."}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </SimpleDialog>
  )
}
